import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { usePlan } from "@/hooks/usePlan";
import { useMigrationRequest } from "@/hooks/useMigration";
import { useUserPreferences, useDismissMigrationBanner } from "@/hooks/useUserPreferences";
import { format } from "date-fns";

const STATUS_LABELS: Record<string, string> = {
  submitted: "Submitted",
  in_review: "In review",
  in_progress: "In progress",
  completed: "Complete",
};

export function MigrationBanner() {
  const navigate = useNavigate();
  const { isProfessional, isStarter } = usePlan();
  const { data: request, isLoading } = useMigrationRequest();
  const { data: prefs, isLoading: loadingPrefs } = useUserPreferences();
  const dismissBanner = useDismissMigrationBanner();

  if (isLoading || loadingPrefs) return null;

  const isDismissed = prefs?.migration_banner_dismissed === true;

  // Post-submission status indicator
  if (request && ["submitted", "in_review", "in_progress"].includes(request.status)) {
    return (
      <div
        className="rounded-xl p-4 flex items-start gap-3 animate-fade-in border-l-4"
        style={{ background: "#ECFDF5", borderLeftColor: "#52B788" }}
      >
        <span className="text-lg">📦</span>
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium font-body text-[#0D1F14]">Your migration is in progress</p>
          <div className="text-xs text-muted-foreground font-body space-y-0.5">
            <p>Status: {STATUS_LABELS[request.status] || request.status}</p>
            <p>Submitted: {format(new Date(request.submitted_at), "d MMM yyyy")}</p>
            <p>Estimated completion: within 2 working days</p>
          </div>
          <div className="flex items-center gap-1 pt-1">
            {["submitted", "in_review", "in_progress", "completed"].map((step, i) => {
              const steps = ["submitted", "in_review", "in_progress", "completed"];
              const currentIdx = steps.indexOf(request.status);
              const isComplete = i <= currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <div key={step} className="flex items-center">
                  <div className={`w-2.5 h-2.5 rounded-full ${isComplete ? "bg-[#52B788]" : "bg-gray-200"} ${isCurrent ? "ring-2 ring-[#52B788] ring-offset-1" : ""}`} />
                  {i < 3 && <div className={`w-6 h-0.5 ${isComplete && i < currentIdx ? "bg-[#52B788]" : "bg-gray-200"}`} />}
                </div>
              );
            })}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 text-xs"
          onClick={() => navigate("/settings?tab=migration")}
        >
          View migration details →
        </Button>
      </div>
    );
  }

  // Completed banner (show for 7 days)
  if (request?.status === "completed") {
    const completedAt = request.completed_at ? new Date(request.completed_at) : new Date();
    const sevenDaysAfter = new Date(completedAt.getTime() + 7 * 24 * 60 * 60 * 1000);
    if (Date.now() > sevenDaysAfter.getTime()) return null;

    return (
      <div
        className="rounded-xl p-4 flex items-center gap-3 animate-fade-in border-l-4"
        style={{ background: "#ECFDF5", borderLeftColor: "#52B788" }}
      >
        <span className="text-lg">✅</span>
        <p className="text-sm font-body text-[#0D1F14] flex-1">
          Your migration is complete. Your records are live in Nursly.
        </p>
        <Button size="sm" onClick={() => navigate("/children")} style={{ background: "#B45309" }}>
          View Children
        </Button>
      </div>
    );
  }

  // Don't show promotional banners if dismissed
  if (isDismissed) return null;

  const handleDismiss = () => {
    dismissBanner();
  };

  // Professional plan - promotional
  if (isProfessional) {
    return (
      <div
        className="rounded-xl p-4 flex items-start gap-3 animate-fade-in border-l-4"
        style={{ background: "#ECFDF5", borderLeftColor: "#52B788" }}
      >
        <span className="text-lg">📦</span>
        <div className="flex-1">
          <p className="text-sm font-medium font-body text-[#0D1F14]">Free migration included with your plan</p>
          <p className="text-xs text-muted-foreground font-body mt-0.5">
            We will move your existing children records, staff profiles, DBS dates and room assignments into Nursly for you. No manual re-entry. No disruption.
          </p>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Button size="sm" className="h-auto whitespace-normal text-left max-w-xs" onClick={() => navigate("/settings?tab=migration")} style={{ background: "#B45309" }}>
            Start your free migration →
          </Button>
          <button onClick={handleDismiss} className="p-1 rounded hover:bg-muted transition-colors" aria-label="Dismiss">
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>
    );
  }

  // Starter plan - promotional with upgrade
  if (isStarter) {
    return (
      <div
        className="rounded-xl p-4 flex items-start gap-3 animate-fade-in border-l-4"
        style={{ background: "#ECFDF5", borderLeftColor: "#52B788" }}
      >
        <span className="text-lg">📦</span>
        <div className="flex-1">
          <p className="text-sm font-medium font-body text-[#0D1F14]">Move your existing records into Nursly</p>
          <p className="text-xs text-muted-foreground font-body mt-0.5">
            Free migration is included with the Professional plan. We move your children records, staff profiles, DBS dates and room assignments for you. No manual re-entry.
          </p>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Button size="sm" className="h-auto whitespace-normal text-left max-w-xs" onClick={() => navigate("/settings?tab=migration")} style={{ background: "#B45309" }}>
            Upgrade to Professional, from £49 a month, or 2 months free when you pay yearly. →
          </Button>
          <button onClick={handleDismiss} className="p-1 rounded hover:bg-muted transition-colors" aria-label="Dismiss">
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
